import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { User, Mail, Phone, Calendar, Lock, Save, Eye, EyeOff, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AccountProfile = () => {
  const { profile, loadProfile } = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    date_of_birth: '',
    gender: ''
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        phone: profile.phone || '',
        date_of_birth: profile.date_of_birth || '',
        gender: profile.gender || ''
      });
    }
  }, [profile]);

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/account/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadProfile();
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/account/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      toast.success('Password changed successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="account-profile">
      <h1 className="font-heading text-2xl font-light text-charcoal">Profile Settings</h1>

      {/* Profile Information */}
      <div className="bg-white border border-charcoal/10 p-6">
        <h2 className="font-heading text-lg font-light text-charcoal mb-6 flex items-center gap-2">
          <User className="w-5 h-5 text-gold" />
          Personal Information
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Full Name */}
          <div>
            <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
              Full Name
            </label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
            />
          </div>

          {/* Email (Read Only) */}
          <div>
            <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
              Email Address
            </label>
            <div className="relative">
              <input
                type="email"
                value={profile?.email || ''}
                disabled
                className="w-full h-11 px-4 pr-10 border border-charcoal/10 bg-beige/30 text-charcoal-light cursor-not-allowed"
              />
              <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-600" />
            </div>
            <p className="text-xs text-charcoal-light mt-1">Email cannot be changed</p>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
              placeholder="+91 9876543210"
            />
          </div>

          {/* Date of Birth */}
          <div>
            <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
              Date of Birth
            </label>
            <input
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
              className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
            />
          </div>

          {/* Gender */}
          <div className="md:col-span-2">
            <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
              Gender
            </label>
            <div className="flex gap-4">
              {['Male', 'Female', 'Other', 'Prefer not to say'].map(option => (
                <label
                  key={option}
                  className={`flex items-center gap-2 px-4 py-2 border cursor-pointer transition-colors ${
                    formData.gender === option
                      ? 'border-gold bg-gold/10 text-gold'
                      : 'border-charcoal/10 text-charcoal-light hover:border-gold/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="gender"
                    value={option}
                    checked={formData.gender === option}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    className="sr-only"
                  />
                  <span className="text-sm">{option}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-charcoal/10 flex justify-end">
          <button
            onClick={handleSaveProfile}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2 bg-gold text-white text-sm font-medium hover:bg-gold-light disabled:opacity-50 transition-colors"
          >
            <Save className="w-4 h-4" />
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Change Password */}
      <div className="bg-white border border-charcoal/10 p-6">
        <h2 className="font-heading text-lg font-light text-charcoal mb-6 flex items-center gap-2">
          <Lock className="w-5 h-5 text-gold" />
          Change Password
        </h2>

        <div className="space-y-4 max-w-md">
          {/* Current Password */}
          <div>
            <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                className="w-full h-11 px-4 pr-10 border border-charcoal/10 focus:border-gold outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPasswords(p => ({ ...p, current: !p.current }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal-light hover:text-charcoal"
              >
                {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                className="w-full h-11 px-4 pr-10 border border-charcoal/10 focus:border-gold outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPasswords(p => ({ ...p, new: !p.new }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal-light hover:text-charcoal"
              >
                {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                className="w-full h-11 px-4 pr-10 border border-charcoal/10 focus:border-gold outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPasswords(p => ({ ...p, confirm: !p.confirm }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal-light hover:text-charcoal"
              >
                {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            onClick={handleChangePassword}
            disabled={changingPassword || !passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password}
            className="flex items-center gap-2 px-6 py-2 bg-charcoal text-white text-sm font-medium hover:bg-charcoal/80 disabled:opacity-50 transition-colors"
          >
            <Lock className="w-4 h-4" />
            {changingPassword ? 'Changing...' : 'Change Password'}
          </button>
        </div>
      </div>

      {/* Account Info */}
      <div className="bg-beige/50 p-6">
        <h3 className="font-heading text-lg font-light text-charcoal mb-4">Account Information</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-charcoal-light">Member Since</p>
            <p className="text-charcoal font-medium">
              {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-charcoal-light">Total Orders</p>
            <p className="text-charcoal font-medium">{profile?.total_orders || 0}</p>
          </div>
          <div>
            <p className="text-charcoal-light">Total Spent</p>
            <p className="text-charcoal font-medium">
              {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(profile?.total_spent || 0)}
            </p>
          </div>
          <div>
            <p className="text-charcoal-light">Loyalty Points</p>
            <p className="text-gold font-medium">{profile?.loyalty_points || 0}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountProfile;
